<script setup lang="ts">
const currentYear = new Date().getFullYear()
</script>

<template>
  <footer class="bg-white border-t border-[#EBEEF5] py-8 mt-16">
    <div class="container mx-auto px-4 max-w-7xl">
      <!-- Top Row: Logo + Contacts + Social -->
      <div
        class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6 pb-6 border-b border-[#EBEEF5]"
      >
        <!-- Logo -->
        <img
          src="@/shared/assets/svg/logo.svg"
          alt="MarketAssist"
          class="h-8"
        >

        <!-- Contacts inline -->
        <div class="flex flex-wrap items-center gap-6">
          <a
            href="https://t.me/marketassist_support"
            target="_blank"
            rel="noopener"
            class="inline-flex items-center text-[#36485C] hover:text-[#172026] transition-colors text-sm"
          >
            <svg
              class="w-4 h-4 mr-2 text-[#9117EF]"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                d="M21.944 2.567a1.342 1.342 0 0 0-1.406-.413L2.614 8.763a1.342 1.342 0 0 0 .12 2.533l4.553 1.706 2.252 6.13a1.342 1.342 0 0 0 2.408.336l2.677-3.345 4.301 3.308a1.342 1.342 0 0 0 2.15-.721l3.869-15.822a1.342 1.342 0 0 0-.5-1.381z"
              />
            </svg>
            @marketassist_support
          </a>
          <a
            href="mailto:support@marketassist.ru"
            class="inline-flex items-center text-[#36485C] hover:text-[#172026] transition-colors text-sm"
          >
            <svg
              class="w-4 h-4 mr-2 text-[#9117EF]"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                d="M20,8L12,13L4,8V6L12,11L20,6M20,4H4C2.89,4 2,4.89 2,6V18A2,2 0 0,0 4,20H20A2,2 0 0,0 22,18V6C22,4.89 21.1,4 20,4Z"
              />
            </svg>
            support@marketassist.ru
          </a>
        </div>

        <!-- Social: Присоединяйтесь к нам -->
        <div class="flex items-center gap-3">
          <span class="text-[#36485C] text-sm">Присоединяйтесь к нам:</span>
          <a
            href="https://t.me/marketassist_support"
            target="_blank"
            rel="noopener"
            class="w-8 h-8 bg-[#F4F8FA] rounded-full flex items-center justify-center hover:bg-[#EFE8FA] transition-colors"
            title="Telegram"
          >
            <svg
              class="w-4 h-4 text-[#36485C]"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                d="M21.944 2.567a1.342 1.342 0 0 0-1.406-.413L2.614 8.763a1.342 1.342 0 0 0 .12 2.533l4.553 1.706 2.252 6.13a1.342 1.342 0 0 0 2.408.336l2.677-3.345 4.301 3.308a1.342 1.342 0 0 0 2.15-.721l3.869-15.822a1.342 1.342 0 0 0-.5-1.381z"
              />
            </svg>
          </a>
        </div>
      </div>

      <!-- Bottom Row: 3 Columns -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-8 mb-6">
        <!-- Column 1: Branding -->
        <div>
          <p class="text-[#36485C] text-sm leading-relaxed">
            {{ $t("footer_promo_text") }}
          </p>
        </div>

        <!-- Column 2: Юридические данные -->
        <div>
          <h4 class="text-[#172026] font-semibold text-sm mb-2">
            {{ $t("footer_legal_title") }}
          </h4>
          <div class="text-[#36485C] text-sm space-y-0.5">
            <p>ООО «Ай Ти Решения»</p>
            <p>Общество с ограниченной ответственностью «Ай Ти Решения»</p>
            <p>ИНН: 9729417254</p>
            <p>ОГРН: 1267700092747</p>
          </div>
        </div>

        <!-- Column 3: Адрес -->
        <div>
          <h4 class="text-[#172026] font-semibold text-sm mb-2">
            {{ $t("footer_address_title") }}
          </h4>
          <p class="text-[#36485C] text-sm leading-relaxed">
            119634, Россия, Москва г.,<br >
            муниципальный округ Ново-Переделкино вн.тер.г.,<br >
            ш. Боровское, д. 37, к. 3, помещ. 1
          </p>
        </div>
      </div>

      <!-- Copyright + Links -->
      <div class="border-t border-[#EBEEF5] pt-4">
        <div
          class="flex flex-col md:flex-row justify-between items-center gap-3"
        >
          <p class="text-[#9CA3AF] text-xs">
            {{ $t("footer_copyright_text", { year: currentYear }) }}
          </p>
          <nav class="flex gap-4">
            <RouterLink
              to="/privacy"
              class="text-[#9CA3AF] hover:text-[#36485C] transition-colors text-xs"
            >
              {{ $t("privacy_policy") }}
            </RouterLink>
            <RouterLink
              to="/terms-of-use"
              class="text-[#9CA3AF] hover:text-[#36485C] transition-colors text-xs"
            >
              {{ $t("terms_of_use") }}
            </RouterLink>
            <RouterLink
              to="/cookie-policy"
              class="text-[#9CA3AF] hover:text-[#36485C] transition-colors text-xs"
            >
              {{ $t("cookie_policy") }}
            </RouterLink>
            <RouterLink
              to="/consent"
              class="text-[#9CA3AF] hover:text-[#36485C] transition-colors text-xs"
            >
              {{ $t("consent_policy") }}
            </RouterLink>
          </nav>
        </div>
      </div>
    </div>
  </footer>
</template>
